import type { CondensedObservation } from '@/shared/schema/observation';
import type { Concept } from '@/shared/types';
import type { Observation } from '@/shared/types/observation';

// *--------------------------- Mapper Helpers----------------------------------*
const toCondensedObservation = (obs: Observation): CondensedObservation => {
  return {
    uuid: obs.uuid,
    display: obs.display,
    concept: {
      uuid: obs.concept.uuid,
      display: obs.concept.display,
    },
    value:
      typeof obs.value === 'object' && obs.value !== null
        ? { uuid: obs.value.uuid, display: obs.value.display }
        : obs.value,
    obsDatetime: obs.obsDatetime,
    encounterUuid: obs.encounter?.uuid,
    voided: obs.voided,
    groupMembers: obs.groupMembers?.map(toCondensedObservation),
  };
};

// *--------------------------- Payload Preparation Helpers----------------------------------*
const getValue = (concept: Concept, values: Record<string, any>) => {
  const { datatype, uuid } = concept;
  const value = values[uuid];

  if (value === null || value === undefined) {
    return null;
  }

  if (['NM', 'ST', 'DT', 'TM', 'TS'].includes(datatype.hl7Abbreviation)) {
    return value;
  }
  if (datatype.hl7Abbreviation === 'CWE') {
    return { uuid: value };
  }

  return null;
};

const createObservation = (
  concept: { uuid: string },
  groupMembers: Array<any> | null,
  value: any | null,
  uuid?: string,
) => {
  return {
    concept: concept.uuid,
    ...(uuid && { uuid }),
    ...(groupMembers && groupMembers.length > 0 && { groupMembers }),
    ...(value !== null && value !== undefined && { value }),
  };
};

const createGroupMember = (member: Concept, values: Record<string, any>) => {
  const value = getValue(member, values);
  if (value === null || value === undefined) return null;
  return createObservation({ uuid: member.uuid }, null, value);
};

const prepareObservationPayload = (concept: Concept, values: Record<string, any>, existingUuid?: string) => {
  if (!concept) return null;
  if (concept?.setMembers && concept?.setMembers?.length > 0) {
    const groupMembers = concept?.setMembers
      ?.map((member: Concept) => createGroupMember(member, values))
      ?.filter((member: any) => member !== null);

    if (groupMembers?.length === 0) return null;

    return createObservation({ uuid: concept.uuid }, groupMembers, null, existingUuid);
  }

  const value = getValue(concept, values);
  if (value === null || value === undefined) return null;

  return createObservation({ uuid: concept.uuid }, null, value, existingUuid);
};

export { toCondensedObservation, createGroupMember, createObservation, getValue, prepareObservationPayload };
